/// <reference lib="dom" />

declare class ResizeObserver {
  constructor(callback: ResizeObserverCallback);
  observe(target: Element, options?: ResizeObserverOptions): void;
  unobserve(target: Element): void;
  disconnect(): void;
}
declare type ResizeObserverCallback = (
  entries: ResizeObserverEntry[],
  observer: ResizeObserver
) => void;
declare interface ResizeObserverEntry {
  readonly target: Element;
  readonly contentRect: DOMRectReadOnly;
}
declare interface ResizeObserverOptions {
  box?: "content-box" | "border-box" | "device-pixel-content-box";
}

import {
  Component,
  ElementRef,
  Input,
  NgZone,
  OnChanges,
  OnDestroy,
  OnInit,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { MobileSessionService } from "../../Services/mobile-session.service";

type GestureMode =
  | "1finger"
  | "zoom-in"
  | "zoom-out"
  | "2finger-swipe"
  | "3finger-swipe";

@Component({
  selector: "ze-mobile-stream-view",
  templateUrl: "./mobile-stream-view.component.html",
  styleUrls: ["./mobile-stream-view.component.scss"],
})
export class MobileStreamViewComponent implements OnInit, OnChanges, OnDestroy {
  @Input() wsUrl: string = "";

  @ViewChild("screenCanvas", { static: true })
  canvasRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("overlayCanvas", { static: true })
  overlayRef!: ElementRef<HTMLCanvasElement>;
  @ViewChild("screenImage", { static: true })
  imageRef!: ElementRef<HTMLImageElement>;
  @ViewChild("screenWrapper", { static: true })
  wrapperRef!: ElementRef<HTMLDivElement>;

  private socket!: WebSocket;
  private overlayTimer: any = null;
  private holdTimer: any = null;
  private isHolding = false;
  private eventsAttached = false;
  private resizeObserver?: ResizeObserver;

  // ── Bitmap rendering pipeline (mirrors device-screen approach) ─────────────
  private pendingBlob: Blob | null = null;
  private isBitmapRendering = false;

  // ── Refresh state ──────────────────────────────────────────────────────────
  isRefreshing = false;

  // ── Dynamic screen height (set from real device aspect ratio) ─────────────
  screenHeight = "700px";

  isLocked = true;
  gestureMode: GestureMode = "1finger";

  readonly multiFingerButtons: {
    mode: GestureMode;
    label: string;
    icon: string;
  }[] = [
    { mode: "zoom-in", label: "Zoom In", icon: "🔍+" },
    { mode: "zoom-out", label: "Zoom Out", icon: "🔍−" },
    { mode: "2finger-swipe", label: "2-Finger Swipe", icon: "✌️" },
    { mode: "3finger-swipe", label: "3-Finger Swipe", icon: "🖐️" },
  ];

  constructor(private session: MobileSessionService, private ngZone: NgZone) {}

  ngOnInit() {
    if (this.wsUrl) this.initStream(this.wsUrl);
  }

  ngOnChanges(changes: SimpleChanges) {
    if (
      changes["wsUrl"] &&
      changes["wsUrl"].currentValue &&
      !changes["wsUrl"].firstChange
    ) {
      this.teardownSocket();
      this.initStream(changes["wsUrl"].currentValue);
    }
  }

  // ── Public: Refresh stream (reconnect WS, re-fetch resolution) ────────────
  refreshStream(): void {
    if (this.isRefreshing) return;
    this.isRefreshing = true;

    // Close socket without clearing the canvas — last frame stays visible
    if (this.socket) {
      this.socket.onclose = null; // suppress side-effects
      this.socket.close(1000, "Refresh");
    }
    this.isBitmapRendering = false;
    this.pendingBlob = null;

    setTimeout(async () => {
      // Re-fetch authoritative device resolution so coordinates stay accurate
      await this.fetchDeviceResolution(this.resolveStreamUrl(this.wsUrl));
      this.connectWebSocket(this.resolveStreamUrl(this.wsUrl));
      this.isRefreshing = false;
    }, 600);
  }

  // ── Stream initialisation ─────────────────────────────────────────────────
  private async initStream(url: string) {
    // Reset resolution — will be set authoritatively by /window/rect
    this.session.deviceResolutionResolved = false;
    this.session.imageWidth = 0;
    this.session.imageHeight = 0;

    const resolved = this.resolveStreamUrl(url);
    await this.fetchDeviceResolution(resolved);

    this.connectWebSocket(resolved);

    // Attach pointer events exactly once — they survive reconnects
    if (!this.eventsAttached) {
      this.setupCanvasEvents();
      this.eventsAttached = true;
    }
  }

  // ── Fetch real device resolution from /window/rect ────────────────────────
  private async fetchDeviceResolution(resolvedUrl: string): Promise<void> {
    try {
      const match = resolvedUrl.match(
        /^wss:\/\/([^/]+)\/android-stream\/([^/]+)/
      );
      if (match) {
        const [, domain, sessionId] = match;
        const rectUrl = `https://${domain}/wd/hub/session/${sessionId}/window/rect`;
        const response = await fetch(rectUrl);
        if (response.ok) {
          const data = await response.json();
          const { width = 0, height = 0 } = data.value || {};
          if (width > 0 && height > 0) {
            this.session.imageWidth = width;
            this.session.imageHeight = height;
            this.session.deviceResolutionResolved = true;
            this.updateScreenHeight(width, height);
            console.log(
              `[MobileStream] Real device resolution: ${width}x${height}`
            );
          }
        } else {
          console.warn(
            `[MobileStream] /window/rect returned ${response.status} — will fall back to frame size`
          );
        }
      }
    } catch (err) {
      console.warn(
        "[MobileStream] Could not fetch device resolution — will fall back to frame size:",
        err
      );
    }
  }

  /**
   * Derive screen-wrapper height from the real device aspect ratio.
   * Caps the height so it never exceeds the available container height,
   * which prevents clipping at higher browser zoom / screen resolution levels.
   */
  private updateScreenHeight(deviceW: number, deviceH: number): void {
    this.ngZone.run(() => {
      const wrapper = this.wrapperRef.nativeElement;
      if (!wrapper) { this.screenHeight = "700px"; return; }

      // Width-driven ideal height based on aspect ratio
      const innerW = wrapper.clientWidth || 332;
      const idealH = Math.round((innerW * deviceH) / deviceW);

      // Available height from the parent container (device-container)
      const parent = wrapper.parentElement;
      const availableH = parent ? parent.clientHeight : window.innerHeight;

      // Use the smaller of ideal vs available, with a small safety margin
      const finalH = availableH > 0 ? Math.min(idealH, availableH - 16) : idealH;
      this.screenHeight = finalH + "px";
    });
  }

  private resolveStreamUrl(url: string): string {
    let r = url.replace(/^ws:\/\//, "wss://");
    r = r.replace(/(wss:\/\/[^/]+):\d+/, "$1");
    return r;
  }

  setGestureMode(mode: GestureMode) {
    this.gestureMode = mode;
  }

  // ── WebSocket ──────────────────────────────────────────────────────────────
  connectWebSocket(streamUrl: string) {
    this.socket = new WebSocket(streamUrl);
    this.socket.binaryType = "blob";

    this.socket.onopen = () =>
      console.log("[MobileStream] WS connected:", streamUrl);

    this.socket.onmessage = (event: MessageEvent) => {
      if (this.isRefreshing) return; // drop stale frames during reconnect
      this.pendingBlob = event.data as Blob;
      if (!this.isBitmapRendering) {
        this.isBitmapRendering = true;
        requestAnimationFrame(() => this.renderFrame());
      }
    };

    this.socket.onerror = (e) => console.error("[MobileStream] WS error:", e);
    this.socket.onclose = () => console.log("[MobileStream] WS closed");
  }

  // ── Last-frame cache — redrawn after any canvas resize to prevent blanking ──
  private lastBitmap: ImageBitmap | null = null;

  // ── Bitmap rendering pipeline ──────────────────────────────────────────────
  private renderFrame(): void {
    const currentBlob = this.pendingBlob;
    this.pendingBlob = null;

    if (!currentBlob) {
      if (this.socket && this.socket.readyState === WebSocket.OPEN) {
        requestAnimationFrame(() => this.renderFrame());
      } else {
        this.isBitmapRendering = false;
      }
      return;
    }

    createImageBitmap(currentBlob)
      .then((bitmap) => {
        const canvas = this.canvasRef.nativeElement;
        const ctx = canvas.getContext("2d");

        // Resolution fallback — only if /window/rect was unreachable
        if (
          !this.session.deviceResolutionResolved &&
          bitmap.width > 0 &&
          bitmap.height > 0
        ) {
          const scale = bitmap.width <= 600 ? 2 : 1;
          this.session.imageWidth = bitmap.width * scale;
          this.session.imageHeight = bitmap.height * scale;
          this.session.deviceResolutionResolved = true;
          this.updateScreenHeight(this.session.imageWidth, this.session.imageHeight);
          console.warn(
            `[MobileStream] Falling back to frame size: ${bitmap.width}x${bitmap.height} (x${scale})`
          );
        }

        // Sync canvas buffer to its CSS footprint.
        // Assigning canvas.width/height clears the bitmap — redraw the last
        // frame immediately so the screen never flashes blank on resize.
        const w = canvas.clientWidth;
        const h = canvas.clientHeight;
        if (w > 0 && h > 0 && (canvas.width !== w || canvas.height !== h)) {
          canvas.width = w;
          canvas.height = h;
          // Repaint last frame first so there is no blank flash
          if (ctx && this.lastBitmap) {
            ctx.drawImage(this.lastBitmap, 0, 0, canvas.width, canvas.height);
          }
        }

        if (ctx && canvas.width > 0 && canvas.height > 0) {
          ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
        }

        // Cache this frame; release the previous one
        if (this.lastBitmap) this.lastBitmap.close();
        this.lastBitmap = bitmap;

        requestAnimationFrame(() => this.renderFrame());
      })
      .catch(() => requestAnimationFrame(() => this.renderFrame()));
  }

  // ── Teardown helpers ───────────────────────────────────────────────────────
  private teardownSocket(): void {
    if (this.socket) this.socket.close();
    this.isBitmapRendering = false;
    this.pendingBlob = null;
    if (this.lastBitmap) { this.lastBitmap.close(); this.lastBitmap = null; }
  }

  // ── Canvas events — attached ONCE, survive reconnects ─────────────────────
  setupCanvasEvents() {
    const canvas = this.canvasRef.nativeElement;
    const overlay = this.overlayRef.nativeElement;

    const syncCanvasSize = () => {
      [canvas, overlay].forEach((c) => {
        const w = c.clientWidth;
        const h = c.clientHeight;
        if (w > 0 && h > 0 && (c.width !== w || c.height !== h)) {
          c.width = w;
          c.height = h;
        }
      });
    };

    const ro = new ResizeObserver(() => {
      syncCanvasSize();
      // Re-derive screen height whenever the wrapper is resized (e.g. browser zoom change)
      if (this.session.imageWidth > 0 && this.session.imageHeight > 0) {
        this.updateScreenHeight(this.session.imageWidth, this.session.imageHeight);
      }
    });
    ro.observe(canvas);
    // Also observe the wrapper's parent so container height changes trigger a recalc
    if (this.wrapperRef.nativeElement.parentElement) {
      ro.observe(this.wrapperRef.nativeElement.parentElement);
    }
    this.resizeObserver = ro;

    const syncOverlay = () => {
      const w = canvas.clientWidth;
      const h = canvas.clientHeight;
      if (w > 0 && h > 0 && (overlay.width !== w || overlay.height !== h)) {
        overlay.width = w;
        overlay.height = h;
      }
    };

    /**
     * Map browser client coords → CSS canvas position + real device coords.
     *
     * Always uses canvas.getBoundingClientRect() so the origin is exactly the
     * canvas top-left — eliminates drift from speaker/camera notch divs above
     * the canvas inside the wrapper.  Re-reads the rect on every event, which
     * means coordinates remain correct after a refresh or resolution change
     * without needing to rebind any event handlers.
     */
    const clientToReal = (clientX: number, clientY: number) => {
      const rect = canvas.getBoundingClientRect();
      if (!rect.width || !rect.height) return null;
      if (!this.session.imageWidth || !this.session.imageHeight) return null;

      const cssX = Math.max(0, Math.min(clientX - rect.left, rect.width));
      const cssY = Math.max(0, Math.min(clientY - rect.top, rect.height));

      const realX = Math.round((cssX * this.session.imageWidth) / rect.width);
      const realY = Math.round((cssY * this.session.imageHeight) / rect.height);

      return { cssX, cssY, realX, realY };
    };

    const clearOverlay = () => {
      syncOverlay();
      overlay.getContext("2d")!.clearRect(0, 0, overlay.width, overlay.height);
    };

    const scheduleOverlayClear = (ms = 400) => {
      if (this.overlayTimer) clearTimeout(this.overlayTimer);
      this.overlayTimer = setTimeout(() => {
        clearOverlay();
        this.overlayTimer = null;
      }, ms);
    };

    // ── Draw helpers ──────────────────────────────────────────────────────────
    const drawDot = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string) => {
      ctx.beginPath(); ctx.arc(x, y, r, 0, 2 * Math.PI);
      ctx.fillStyle = color; ctx.fill();
    };
    const drawRing = (ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string, lw = 3) => {
      ctx.beginPath(); ctx.arc(x, y, r, 0, 2 * Math.PI);
      ctx.strokeStyle = color; ctx.lineWidth = lw; ctx.stroke();
    };

    const drawTapIndicator = (cssX: number, cssY: number, color = "rgba(255,80,80,0.9)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save(); drawRing(ctx, cssX, cssY, 20, color); drawDot(ctx, cssX, cssY, 5, color); ctx.restore();
      scheduleOverlayClear(400);
    };

    const drawDoubleTapIndicator = (cssX: number, cssY: number) => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save();
      [12, 24].forEach((r) => drawRing(ctx, cssX, cssY, r, "rgba(255,200,0,0.9)", 2));
      drawDot(ctx, cssX, cssY, 5, "rgba(255,200,0,0.9)");
      ctx.restore(); scheduleOverlayClear(500);
    };

    const drawHoldIndicator = (cssX: number, cssY: number) => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save(); drawRing(ctx, cssX, cssY, 22, "rgba(160,80,255,0.9)", 4); drawDot(ctx, cssX, cssY, 8, "rgba(160,80,255,0.7)"); ctx.restore();
    };

    const drawSwipeStart = (cssX: number, cssY: number, color = "rgba(80,200,255,0.9)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save(); drawDot(ctx, cssX, cssY, 8, color); drawRing(ctx, cssX, cssY, 8, "#fff", 2); ctx.restore();
    };

    const drawSwipeLine = (x1: number, y1: number, x2: number, y2: number, color = "rgba(80,200,255,0.7)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]);
      drawDot(ctx, x1, y1, 8, color.replace("0.7", "0.9")); ctx.restore();
    };

    const drawSwipeFinal = (x1: number, y1: number, x2: number, y2: number, color = "rgba(80,200,255,0.9)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save();
      ctx.beginPath(); ctx.moveTo(x1, y1); ctx.lineTo(x2, y2);
      ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.stroke();
      const a = Math.atan2(y2 - y1, x2 - x1);
      ctx.beginPath();
      ctx.moveTo(x2, y2);
      ctx.lineTo(x2 - 15 * Math.cos(a - 0.4), y2 - 15 * Math.sin(a - 0.4));
      ctx.lineTo(x2 - 15 * Math.cos(a + 0.4), y2 - 15 * Math.sin(a + 0.4));
      ctx.closePath(); ctx.fillStyle = color; ctx.fill(); ctx.restore();
      scheduleOverlayClear(500);
    };

    const drawMultiSwipeLine = (starts: { cssX: number; cssY: number }[], ends: { cssX: number; cssY: number }[], color = "rgba(80,200,255,0.7)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save();
      starts.forEach((s, i) => {
        const e = ends[i];
        ctx.beginPath(); ctx.moveTo(s.cssX, s.cssY); ctx.lineTo(e.cssX, e.cssY);
        ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.setLineDash([6, 4]); ctx.stroke(); ctx.setLineDash([]);
        drawDot(ctx, s.cssX, s.cssY, 7, color.replace("0.7", "0.9"));
      });
      ctx.restore();
    };

    const drawMultiSwipeFinal = (starts: { cssX: number; cssY: number }[], ends: { cssX: number; cssY: number }[], color = "rgba(80,200,255,0.9)") => {
      syncOverlay(); clearOverlay();
      const ctx = overlay.getContext("2d")!;
      ctx.save();
      starts.forEach((s, i) => {
        const e = ends[i];
        ctx.beginPath(); ctx.moveTo(s.cssX, s.cssY); ctx.lineTo(e.cssX, e.cssY);
        ctx.strokeStyle = color; ctx.lineWidth = 3; ctx.stroke();
        const a = Math.atan2(e.cssY - s.cssY, e.cssX - s.cssX);
        ctx.beginPath(); ctx.moveTo(e.cssX, e.cssY);
        ctx.lineTo(e.cssX - 12 * Math.cos(a - 0.4), e.cssY - 12 * Math.sin(a - 0.4));
        ctx.lineTo(e.cssX - 12 * Math.cos(a + 0.4), e.cssY - 12 * Math.sin(a + 0.4));
        ctx.closePath(); ctx.fillStyle = color; ctx.fill();
      });
      ctx.restore(); scheduleOverlayClear(500);
    };

    // ── Shared drag state ─────────────────────────────────────────────────────
    let activePointerId: number | null = null;
    let dragStartClientX = 0;
    let dragStartClientY = 0;
    let dragStartReal: { realX: number; realY: number } | null = null;
    let dragStartCss: { cssX: number; cssY: number } | null = null;
    let dragMoved = false;
    let suppressNextClick = false;
    let lastTapTime = 0;
    const DOUBLE_TAP_MS = 300;
    const DRAG_THRESHOLD = 12;

    // ── pointerdown ───────────────────────────────────────────────────────────
    canvas.addEventListener("pointerdown", (ev: PointerEvent) => {
      if (!["touch", "pen", "mouse"].includes(ev.pointerType)) return;
      const coords = clientToReal(ev.clientX, ev.clientY);
      if (!coords) return;
      try { canvas.setPointerCapture(ev.pointerId); } catch { /* ignore */ }

      if (this.gestureMode === "zoom-in") {
        drawTapIndicator(coords.cssX, coords.cssY, "rgba(80,255,160,0.9)");
        this.sendZoomInAtPoint(coords.realX, coords.realY); return;
      }
      if (this.gestureMode === "zoom-out") {
        drawTapIndicator(coords.cssX, coords.cssY, "rgba(255,160,80,0.9)");
        this.sendZoomOutAtPoint(coords.realX, coords.realY); return;
      }

      activePointerId = ev.pointerId;
      dragStartClientX = ev.clientX; dragStartClientY = ev.clientY;
      dragStartReal = { realX: coords.realX, realY: coords.realY };
      dragStartCss = { cssX: coords.cssX, cssY: coords.cssY };
      dragMoved = false; suppressNextClick = false;

      const dotColor = this.gestureMode === "1finger" ? "rgba(80,200,255,0.9)" : "rgba(130,200,80,0.9)";
      drawSwipeStart(coords.cssX, coords.cssY, dotColor);

      if (this.gestureMode === "1finger") {
        this.isHolding = false;
        if (this.holdTimer) clearTimeout(this.holdTimer);
        this.holdTimer = setTimeout(() => {
          if (!dragMoved && activePointerId !== null && this.gestureMode === "1finger") {
            this.isHolding = true; suppressNextClick = true;
            drawHoldIndicator(coords.cssX, coords.cssY);
            this.sendLongPress(coords.realX, coords.realY);
            scheduleOverlayClear(800);
          }
        }, 600);
      }
    });

    // ── pointermove ───────────────────────────────────────────────────────────
    canvas.addEventListener("pointermove", (ev: PointerEvent) => {
      if (activePointerId === null || ev.pointerId !== activePointerId) return;
      if (!dragStartCss || !dragStartReal) return;
      const dx = ev.clientX - dragStartClientX;
      const dy = ev.clientY - dragStartClientY;
      if (Math.sqrt(dx * dx + dy * dy) <= DRAG_THRESHOLD) return;

      dragMoved = true;
      if (this.holdTimer) { clearTimeout(this.holdTimer); this.holdTimer = null; }

      const end = clientToReal(ev.clientX, ev.clientY);
      if (!end) return;

      if (this.gestureMode === "1finger") {
        drawSwipeLine(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY);
      } else if (this.gestureMode === "2finger-swipe") {
        const { starts, ends } = this.get2FingerPositions(dragStartCss, end, canvas);
        drawMultiSwipeLine(starts, ends);
      } else if (this.gestureMode === "3finger-swipe") {
        const { starts, ends } = this.get3FingerPositions(dragStartCss, end, canvas);
        drawMultiSwipeLine(starts, ends);
      } else if (this.gestureMode === "zoom-in") {
        drawSwipeLine(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY, "rgba(80,255,160,0.7)");
      } else if (this.gestureMode === "zoom-out") {
        drawSwipeLine(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY, "rgba(255,160,80,0.7)");
      }
    });

    // ── pointerup ─────────────────────────────────────────────────────────────
    canvas.addEventListener("pointerup", (ev: PointerEvent) => {
      if (activePointerId === null || ev.pointerId !== activePointerId) return;
      activePointerId = null;
      if (this.holdTimer) { clearTimeout(this.holdTimer); this.holdTimer = null; }
      if (this.isHolding) { this.isHolding = false; return; }

      const end = clientToReal(ev.clientX, ev.clientY);
      if (!end || !dragStartReal || !dragStartCss) return;
      const mode = this.gestureMode;

      if (mode === "1finger") {
        if (dragMoved) {
          drawSwipeFinal(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY);
          this.sendSwipeEvent(dragStartReal.realX, dragStartReal.realY, end.realX, end.realY);
          suppressNextClick = true;
        } else { clearOverlay(); }
        return;
      }

      if (!dragMoved) { clearOverlay(); return; }

      if (mode === "2finger-swipe") {
        const { starts, ends, realStarts, realEnds } = this.get2FingerPositions(dragStartCss, end, canvas, dragStartReal, end);
        drawMultiSwipeFinal(starts, ends); this.send2FingerSwipeFromDrag(realStarts, realEnds);
      } else if (mode === "3finger-swipe") {
        const { starts, ends, realStarts, realEnds } = this.get3FingerPositions(dragStartCss, end, canvas, dragStartReal, end);
        drawMultiSwipeFinal(starts, ends); this.send3FingerSwipeFromDrag(realStarts, realEnds);
      } else if (mode === "zoom-in") {
        drawSwipeFinal(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY, "rgba(80,255,160,0.9)");
        this.sendZoomIn(dragStartReal.realX, dragStartReal.realY, end.realX, end.realY);
      } else if (mode === "zoom-out") {
        drawSwipeFinal(dragStartCss.cssX, dragStartCss.cssY, end.cssX, end.cssY, "rgba(255,160,80,0.9)");
        this.sendZoomOut(dragStartReal.realX, dragStartReal.realY, end.realX, end.realY);
      }
    });

    // ── click — single & double tap (1-finger only) ───────────────────────────
    canvas.addEventListener("click", (ev: MouseEvent) => {
      if (this.gestureMode !== "1finger") return;
      if (suppressNextClick) { suppressNextClick = false; return; }
      if (this.isHolding) return;
      const coords = clientToReal(ev.clientX, ev.clientY);
      if (!coords) return;
      const now = Date.now();
      if (now - lastTapTime < DOUBLE_TAP_MS) {
        lastTapTime = 0;
        drawDoubleTapIndicator(coords.cssX, coords.cssY);
        this.sendDoubleTap(coords.realX, coords.realY);
      } else {
        lastTapTime = now;
        setTimeout(() => {
          if (lastTapTime === now) {
            drawTapIndicator(coords.cssX, coords.cssY);
            this.sendClickEvent(coords.realX, coords.realY);
          }
        }, DOUBLE_TAP_MS + 10);
      }
    });

    canvas.addEventListener("pointercancel", () => {
      activePointerId = null; dragMoved = false; this.isHolding = false;
      if (this.holdTimer) { clearTimeout(this.holdTimer); this.holdTimer = null; }
      clearOverlay();
    });
  }

  // ── Multi-finger position helpers ─────────────────────────────────────────
  private fingerOffsetPx(canvas: HTMLCanvasElement): number {
    return Math.round(canvas.clientWidth * 0.12) || 60;
  }

  get2FingerPositions(
    startCss: { cssX: number; cssY: number }, endCss: { cssX: number; cssY: number },
    canvas: HTMLCanvasElement, startReal?: { realX: number; realY: number }, endReal?: { realX: number; realY: number }
  ) {
    const offset = this.fingerOffsetPx(canvas);
    const dx = endCss.cssX - startCss.cssX, dy = endCss.cssY - startCss.cssY;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / len, py = dx / len;
    const starts = [
      { cssX: startCss.cssX + px * offset, cssY: startCss.cssY + py * offset },
      { cssX: startCss.cssX - px * offset, cssY: startCss.cssY - py * offset },
    ];
    const ends = [
      { cssX: endCss.cssX + px * offset, cssY: endCss.cssY + py * offset },
      { cssX: endCss.cssX - px * offset, cssY: endCss.cssY - py * offset },
    ];
    let realStarts: { realX: number; realY: number }[] = [];
    let realEnds: { realX: number; realY: number }[] = [];
    if (startReal && endReal) {
      const rect = canvas.getBoundingClientRect();
      const sx = this.session.imageWidth / rect.width, sy = this.session.imageHeight / rect.height;
      const rpx = px * offset * sx, rpy = py * offset * sy;
      realStarts = [{ realX: Math.round(startReal.realX + rpx), realY: Math.round(startReal.realY + rpy) }, { realX: Math.round(startReal.realX - rpx), realY: Math.round(startReal.realY - rpy) }];
      realEnds   = [{ realX: Math.round(endReal.realX + rpx),   realY: Math.round(endReal.realY + rpy)   }, { realX: Math.round(endReal.realX - rpx),   realY: Math.round(endReal.realY - rpy)   }];
    }
    return { starts, ends, realStarts, realEnds };
  }

  get3FingerPositions(
    startCss: { cssX: number; cssY: number }, endCss: { cssX: number; cssY: number },
    canvas: HTMLCanvasElement, startReal?: { realX: number; realY: number }, endReal?: { realX: number; realY: number }
  ) {
    const offset = this.fingerOffsetPx(canvas);
    const dx = endCss.cssX - startCss.cssX, dy = endCss.cssY - startCss.cssY;
    const len = Math.sqrt(dx * dx + dy * dy) || 1;
    const px = -dy / len, py = dx / len;
    const starts = [
      { cssX: startCss.cssX, cssY: startCss.cssY },
      { cssX: startCss.cssX + px * offset, cssY: startCss.cssY + py * offset },
      { cssX: startCss.cssX - px * offset, cssY: startCss.cssY - py * offset },
    ];
    const ends = [
      { cssX: endCss.cssX, cssY: endCss.cssY },
      { cssX: endCss.cssX + px * offset, cssY: endCss.cssY + py * offset },
      { cssX: endCss.cssX - px * offset, cssY: endCss.cssY - py * offset },
    ];
    let realStarts: { realX: number; realY: number }[] = [];
    let realEnds: { realX: number; realY: number }[] = [];
    if (startReal && endReal) {
      const rect = canvas.getBoundingClientRect();
      const sx = this.session.imageWidth / rect.width, sy = this.session.imageHeight / rect.height;
      const rpx = px * offset * sx, rpy = py * offset * sy;
      realStarts = [{ realX: startReal.realX, realY: startReal.realY }, { realX: Math.round(startReal.realX + rpx), realY: Math.round(startReal.realY + rpy) }, { realX: Math.round(startReal.realX - rpx), realY: Math.round(startReal.realY - rpy) }];
      realEnds   = [{ realX: endReal.realX,   realY: endReal.realY   }, { realX: Math.round(endReal.realX + rpx),   realY: Math.round(endReal.realY + rpy)   }, { realX: Math.round(endReal.realX - rpx),   realY: Math.round(endReal.realY - rpy)   }];
    }
    return { starts, ends, realStarts, realEnds };
  }

  // ── WebSocket senders ──────────────────────────────────────────────────────

  sendClickEvent(x: number, y: number) {
    this.sendWS({ action: "click", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x, y }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendDoubleTap(x: number, y: number) {
    this.sendWS({ action: "click", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x, y }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 80 }, { type: "pointerUp", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 80 }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendLongPress(x: number, y: number) {
    this.sendWS({ action: "click", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x, y }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 1500 }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendSwipeEvent(startX: number, startY: number, endX: number, endY: number) {
    this.sendWS({ action: "swipe", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: startX, y: startY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 0 }, { type: "pointerMove", duration: 500, origin: "viewport", x: endX, y: endY }, { type: "pointerUp", button: 0 }] }] } });
  }

  send2FingerSwipeFromDrag(realStarts: { realX: number; realY: number }[], realEnds: { realX: number; realY: number }[]) {
    this.sendWS({ action: "swipe", args: { actions: realStarts.map((s, i) => ({ type: "pointer", id: `finger${i + 1}`, parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: s.realX, y: s.realY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 500, origin: "viewport", x: realEnds[i].realX, y: realEnds[i].realY }, { type: "pointerUp", button: 0 }] })) } });
  }

  send3FingerSwipeFromDrag(realStarts: { realX: number; realY: number }[], realEnds: { realX: number; realY: number }[]) {
    this.sendWS({ action: "swipe", args: { actions: realStarts.map((s, i) => ({ type: "pointer", id: `finger${i + 1}`, parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: s.realX, y: s.realY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 500, origin: "viewport", x: realEnds[i].realX, y: realEnds[i].realY }, { type: "pointerUp", button: 0 }] })) } });
  }

  sendZoomIn(startX: number, startY: number, endX: number, endY: number) {
    const midX = Math.round((startX + endX) / 2), midY = Math.round((startY + endY) / 2);
    this.sendWS({ action: "swipe", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: midX, y: midY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: startX, y: startY }, { type: "pointerUp", button: 0 }] }, { type: "pointer", id: "finger2", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: midX, y: midY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: endX, y: endY }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendZoomOut(startX: number, startY: number, endX: number, endY: number) {
    const midX = Math.round((startX + endX) / 2), midY = Math.round((startY + endY) / 2);
    this.sendWS({ action: "swipe", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: startX, y: startY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: midX, y: midY }, { type: "pointerUp", button: 0 }] }, { type: "pointer", id: "finger2", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: endX, y: endY }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: midX, y: midY }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendZoomInAtPoint(cx: number, cy: number) {
    const spread = Math.round((this.session.imageWidth || 1080) * 0.2);
    this.sendWS({ action: "swipe", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: cx, y: cy }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: cx - spread, y: cy }, { type: "pointerUp", button: 0 }] }, { type: "pointer", id: "finger2", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: cx, y: cy }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: cx + spread, y: cy }, { type: "pointerUp", button: 0 }] }] } });
  }

  sendZoomOutAtPoint(cx: number, cy: number) {
    const spread = Math.round((this.session.imageWidth || 1080) * 0.2);
    this.sendWS({ action: "swipe", args: { actions: [{ type: "pointer", id: "finger1", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: cx - spread, y: cy }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: cx, y: cy }, { type: "pointerUp", button: 0 }] }, { type: "pointer", id: "finger2", parameters: { pointerType: "touch" }, actions: [{ type: "pointerMove", duration: 0, x: cx + spread, y: cy }, { type: "pointerDown", button: 0 }, { type: "pause", duration: 100 }, { type: "pointerMove", duration: 600, origin: "viewport", x: cx, y: cy }, { type: "pointerUp", button: 0 }] }] } });
  }

  private sendWS(payload: object) {
    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(JSON.stringify(payload));
    }
  }

  toggleLock()      { this.isLocked = !this.isLocked; }
  sendDeviceButton(btn: string) { this.sendWS({ action: btn }); }
  pressBack()       { this.sendDeviceButton("back"); }
  pressHome()       { this.sendDeviceButton("home"); }
  pressRecents()    { this.sendDeviceButton("backgroundApps"); }
  pressVolumeUp()   { this.sendDeviceButton("volumeUp"); }
  pressVolumeDown() { this.sendDeviceButton("volumeDown"); }
  pressPower()      { this.sendDeviceButton("power"); }

  ngOnDestroy() {
    if (this.overlayTimer) clearTimeout(this.overlayTimer);
    if (this.holdTimer) clearTimeout(this.holdTimer);
    if (this.socket) this.socket.close();
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (this.lastBitmap) { this.lastBitmap.close(); this.lastBitmap = null; }
  }
}